import ExcelJS from 'exceljs'; // Assuming ExcelJS is imported where needed
import { runInAction, toJS } from 'mobx'; // Import runInAction and toJS

/**
 * Helper class for managing conditional formatting rules based on column option styles.
 */
export class ConditionalFormattingHelper {

    /**
     * Applies conditional formatting rules to a worksheet column based on the Column model's optionStyles.
     * Clears existing 'cellIs' equal rules managed by this helper for the exact range before applying new ones.
     *
     * @param {ExcelJS.Worksheet} worksheet - The ExcelJS worksheet instance.
     * @param {import('../models/SheetModel').Column} column - The Column model instance containing optionStyles.
     * @param {number} columnIndex - The 1-based index of the column in the worksheet.
     * @param {number} startRow - The 1-based index of the first data row to apply formatting to.
     * @param {number} endRow - The 1-based index of the last data row to apply formatting to.
     */
    static applyConditionalFormatting(worksheet, column, columnIndex, startRow, endRow) {
        if (!worksheet || !column || columnIndex < 1 || startRow > endRow) {
            return;
        }

        // Add debugging logs for the column type
        console.log(`[CFHelper Apply] Column "${column.heading}" - Type check:`, {
            typeId: column.type?.id,
            hasConditionalFormatting: column.type?.hasConditionalFormatting,
            formattingType: column.type?.conditionalFormattingType
        });

        // Clear existing formatting regardless of type
        this.clearConditionalFormatting(worksheet, columnIndex, startRow, endRow);

        // Skip columns that don't support conditional formatting
        if (!column.type?.hasConditionalFormatting) {
            console.log(`[CFHelper Apply] Column "${column.heading}" doesn't support conditional formatting, clearing only`);
            return;
        }

        // Handle different conditional formatting types based on model definition
        if (column.type?.conditionalFormattingType === 'colorScale') {
            console.log(`[CFHelper Apply] Detected colorScale formatting for column "${column.heading}"`);
            return this._applyColorScaleFormatting(worksheet, column, columnIndex, startRow, endRow);
        } else if (column.type?.id === 'CHECKBOX') {
            // Special handling for checkboxes - apply red/green formatting
            console.log(`[CFHelper Apply] Detected checkbox column "${column.heading}", applying TRUE/FALSE formatting`);
            return this._applyCheckboxFormatting(worksheet, column, columnIndex, startRow, endRow);
        } else if (column.type?.conditionalFormattingType === 'cellIs' && column.type?.hasCustomValidation) {
            // Only proceed with cellIs type if we have option styles
            if (!column.optionStyles || column.optionStyles.size === 0) {
                console.log(`[CFHelper Apply] No option styles defined for "${column.heading}", only clearing done.`);
                return;
            }
            
            // Continue with existing logic for cellIs/expression rules
            const columnLetter = worksheet.getColumn(columnIndex).letter;
            const range = `${columnLetter}${startRow}:${columnLetter}${endRow}`;
            const topLeftCellAddress = range.split(':')[0];
            
            console.log(`[CFHelper Apply] Applying 'expression' formatting for column "${column.heading}" (${columnLetter}) range ${range}.`);
            
            // Apply rules based on optionStyles
            let priority = 1;
            column.optionStyles.forEach((styleObject, optionValue) => {
                if (!column.validationOptions.includes(optionValue)) {
                    console.warn(`[CFHelper Apply] Skipping style for option "${optionValue}" as it's no longer a valid option for column "${column.heading}".`);
                    return; // Skip this style
                }

                // --- FIX: Use 'expression' type rule ---
                // Escape double quotes within the option value for the Excel formula
                const escapedOptionValue = optionValue.replace(/"/g, '""');
                // Formula compares the top-left cell of the range to the quoted string
                const formula = `${topLeftCellAddress}="${escapedOptionValue}"`;

                // --- MODIFICATION: Robustly construct the style for the rule ---
                const storedPlainStyle = toJS(styleObject || {}); // Get plain stored style or empty object
                console.log(`[CFHelper Apply DBG] - Stored style for "${optionValue}":`, JSON.stringify(storedPlainStyle));

                const finalRuleStyle = {};

                // 1. Process Fill
                if (storedPlainStyle.fill?.fgColor?.argb) {
                    finalRuleStyle.fill = {
                        type: 'pattern',
                        pattern: 'solid',       // Try changing 'solid' to 'lightGrid' or other pattern
                        fgColor: { argb: storedPlainStyle.fill.fgColor.argb },
                        bgColor: { argb: storedPlainStyle.fill.fgColor.argb },
                    };
                    console.log(`[CFHelper Apply DBG] - Applied pattern color "${storedPlainStyle.fill.fgColor.argb}"`);
                } else {
                     console.warn(`[CFHelper Apply DBG] - Missing fgColor for fill for option "${optionValue}". Skipping fill in rule.`);
                     // Optionally add a default fill if required:
                     // finalRuleStyle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
                }


                // 2. Process Font
                if (storedPlainStyle.font?.color?.argb) {
                    finalRuleStyle.font = {
                        color: { argb: storedPlainStyle.font.color.argb },
                        // Set defaults REQUIRED by ExcelJS for CF font styles
                        name: storedPlainStyle.font.name || 'Arial', // Use stored or default
                        family: storedPlainStyle.font.family || 2,     // Use stored or default
                        size: storedPlainStyle.font.size || 11,       // Use stored or default
                        // **Explicitly set boolean properties to false**
                        bold: false,
                        italic: false,
                        underline: false,
                        strike: false,
                        outline: false,
                    };
                } else {
                    console.warn(`[CFHelper Apply DBG] - Missing color for font for option "${optionValue}". Skipping font in rule.`);
                     // Optionally add a default font if required:
                     // finalRuleStyle.font = { color: { argb: 'FF000000' }, name: 'Calibri', family: 2, size: 11, bold: false, italic: false, underline: false, strike: false, outline: false };
                }

                // 3. Add other properties if they exist and are needed (optional)
                // Example: if (storedPlainStyle.alignment) finalRuleStyle.alignment = storedPlainStyle.alignment;
                // Example: if (storedPlainStyle.border) finalRuleStyle.border = storedPlainStyle.border;
                // Example: if (storedPlainStyle.numFmt) finalRuleStyle.numFmt = storedPlainStyle.numFmt;

                console.log(`[CFHelper Apply DBG] - Final constructed style for rule for "${optionValue}":`, JSON.stringify(finalRuleStyle));

                // Check if the final style object is empty (might happen if both fill/font were skipped)
                if (Object.keys(finalRuleStyle).length === 0) {
                     console.warn(`[CFHelper Apply] - Skipping rule for "${optionValue}" as final style object is empty.`);
                     return; // Continue to next optionValue
                }

                const rule = {
                    type: 'expression',
                    formulae: [formula],
                    style: finalRuleStyle, // Use the carefully constructed style
                    priority: priority,
                };
                // --- END MODIFICATION ---

                console.log(`[CFHelper Apply] - Adding rule for "${optionValue}", priority ${priority}, Final Rule Object:`, JSON.stringify(rule));

                try {
                    worksheet.addConditionalFormatting({
                        ref: range,
                        rules: [rule]
                    });
                    priority++;
                } catch (error) {
                     console.error(`[CFHelper Apply] Error adding conditional formatting rule for "${optionValue}" in column "${column.heading}":`, error);
                     console.error(`[CFHelper Apply] Rule details:`, rule);
                     console.error(`[CFHelper Apply] Range:`, range);
                }
            });
            
            console.log(`[CFHelper Apply] Finished applying ${priority - 1} rules for column "${column.heading}"`);
        } else {
            console.log(`[CFHelper Apply] Column "${column.heading}" has unsupported formatting type: ${column.type?.conditionalFormattingType}`);
        }
    }

    /**
     * Applies color scale conditional formatting to columns that support it
     * @private
     */
    static _applyColorScaleFormatting(worksheet, column, columnIndex, startRow, endRow) {
        const columnLetter = worksheet.getColumn(columnIndex).letter;
        const range = `${columnLetter}${startRow}:${columnLetter}${endRow}`;
        
        console.log(`[CFHelper Apply] Applying color scale formatting for column "${column.heading}" range ${range}`);
        console.log(`[CFHelper Apply] Column type ID: ${column.type?.id}`);
        
        // Add color scale rule
        try {
            // Create rule object first so we can log it
            const colorScaleRule = {
                type: 'colorScale',
                priority: 1,
                cfvo: [
                    { type: 'min' },
                    { type: 'max' }
                ],
                color: [
                    { argb: 'FFFFFFFF' },  // White for low values
                    { argb: 'FFC3F938' },  // Green for max values
                ]
            };
            
            console.log(`[CFHelper Apply] About to add color scale rule:`, JSON.stringify(colorScaleRule));
            
            worksheet.addConditionalFormatting({
                ref: range,
                rules: [colorScaleRule]
            });
            
            console.log(`[CFHelper Apply] Successfully applied color scale rule to range ${range}`);
        } catch (error) {
            console.error(`[CFHelper Apply] Error adding color scale for column "${column.heading}":`, error);
            console.error(`[CFHelper Apply] Error name: ${error.name}, message: ${error.message}`);
        }
    }

    /**
     * Applies conditional formatting for checkbox columns (TRUE=green, FALSE=red)
     * @private
     */
    static _applyCheckboxFormatting(worksheet, column, columnIndex, startRow, endRow) {
        const columnLetter = worksheet.getColumn(columnIndex).letter;
        const range = `${columnLetter}${startRow}:${columnLetter}${endRow}`;
        
        console.log(`[CFHelper Apply] Applying checkbox formatting for column "${column.heading}" range ${range}`);
        
        try {
            // Create rules for TRUE and FALSE values
            const trueRule = {
                type: 'cellIs',
                operator: 'equal',
                formulae: [true],
                priority: 1,
                style: {
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFD5FB73' },  // Green for TRUE
                        bgColor: { argb: 'FFD5FB73' }  // Green for TRUE
                    }
                }
            };
            
            const falseRule = {
                type: 'cellIs',
                operator: 'equal',
                formulae: [false],
                priority: 2,
                style: {
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFF06583' },  // Red for FALSE
                        bgColor: { argb: 'FFF06583' }  // Red for FALSE
                    }
                }
            };
            
            console.log(`[CFHelper Apply] Adding TRUE rule (green) for checkbox column "${column.heading}"`);
            worksheet.addConditionalFormatting({
                ref: range,
                rules: [trueRule]
            });
            
            console.log(`[CFHelper Apply] Adding FALSE rule (red) for checkbox column "${column.heading}"`);
            worksheet.addConditionalFormatting({
                ref: range,
                rules: [falseRule]
            });
            
            console.log(`[CFHelper Apply] Successfully applied checkbox formatting to range ${range}`);
        } catch (error) {
            console.error(`[CFHelper Apply] Error adding checkbox formatting for column "${column.heading}":`, error);
            console.error(`[CFHelper Apply] Error name: ${error.name}, message: ${error.message}`);
        }
    }

    /**
     * Reads 'cellIs'/'equal' conditional formatting rules from a worksheet column
     * and populates the Column model's optionStyles.
     *
     * @param {ExcelJS.Worksheet} worksheet - The ExcelJS worksheet instance.
     * @param {import('../models/SheetModel').Column} column - The Column model instance to populate.
     * @param {number} columnIndex - The 1-based index of the column in the worksheet.
     */
    static readConditionalFormatting(worksheet, column, columnIndex) {
        if (!worksheet || !column) {
            return;
        }
        
        // Check if column supports conditional formatting
        if (!column.type?.hasConditionalFormatting) {
            console.warn(`[CFHelper Read] Skipping read for column "${column?.heading}": Does not support conditional formatting`);
            return;
        }
        
        // Skip types with generated formatting (colorScale and checkbox)
        if (column.type?.conditionalFormattingType === 'colorScale' || column.type?.id === 'CHECKBOX') {
            console.log(`[CFHelper Read] Skipping read for column "${column.heading}" - formatting is generated`);
            return;
        }
        
        // Continue with existing code for 'cellIs' types
        if (!column.type?.hasCustomValidation) {
            console.warn(`[CFHelper Read] Skipping read for column "${column?.heading}": Does not support custom validation`);
            return;
        }

        const columnLetter = worksheet.getColumn(columnIndex).letter;
        console.log(`[CFHelper Read] Reading rules for column "${column.heading}" (${columnLetter})`);

        // Use runInAction because we are modifying the observable map `column.optionStyles`
        runInAction(() => {
            column.clearOptionStyles(); // Clear existing styles before reading

            const foundStyles = new Map(); // Temp map to handle potential priority issues later if needed

            (worksheet.conditionalFormattings || []).forEach(cf => {
                // Basic check: Does the rule's ref apply to our column?
                // This is simplified; a robust solution would parse cf.ref properly.
                // We check if the ref IS the column letter or STARTS with the column letter followed by a number.
                const refAppliesToColumn = cf.ref && (
                    cf.ref === columnLetter || // Applies to whole column? (ExcelJS might not use this format)
                    new RegExp(`^${columnLetter}\\d+(:${columnLetter}\\d+)?$`).test(cf.ref) // e.g., C2 or C2:C100
                );

                if (!refAppliesToColumn) {
                     // console.log(`[CFHelper Read] Skipping rule with ref "${cf.ref}" - does not seem to apply to column ${columnLetter}`);
                     return; // Skip rules not clearly applying to our column
                }

                cf.rules.forEach(rule => {
                    // --- FIX: Look for 'expression' type and parse formula ---
                    if (rule.type === 'expression' && rule.formulae?.length > 0 && rule.style) {
                        const formulaRead = rule.formulae[0];
                        let optionValue = null;

                        // Regex to capture CellRef="StringValue" pattern
                        // Allows optional $ signs for absolute refs, handles spaces around =
                        // Captures the string inside the quotes (Group 2)
                        const match = formulaRead?.match(/^\$?([A-Z]+)\$?(\d+)\s*=\s*"(.*)"$/i);

                        if (match && match[3] !== undefined) {
                             // Extract the string value (Group 3)
                            optionValue = match[3];
                            // Unescape doubled double quotes ("") back to single (")
                            optionValue = optionValue.replace(/""/g, '"');
                        } else {
                             // console.log(`[CFHelper Read] - Skipping expression rule, formula format not recognized: "${formulaRead}"`);
                             return; // Skip if formula doesn't match expected pattern
                        }

                        // Use the extracted optionValue for checks and storage
                        if (optionValue !== null && column.validationOptions.includes(String(optionValue))) {
                            const priority = rule.priority || Infinity;
                            const existing = foundStyles.get(String(optionValue));

                            // --- MODIFICATION: Ensure deep copy when reading ---
                            // Use toJS to potentially handle nested proxies within rule.style if they exist
                            // Although rule.style from ExcelJS should be plain, this adds safety.
                            // Ensure the *entire* style object, including nested font/fill, is copied.
                            const styleToStore = toJS(rule.style);

                            if (!existing || priority < existing.priority) {
                                 foundStyles.set(String(optionValue), { style: styleToStore, priority: priority });
                                 // Log the style we intend to store
                                 console.log(`[CFHelper Read DBG] - Found style for "${optionValue}", stored full style:`, JSON.stringify(styleToStore)); // Log the full stored style
                            }
                            // --- END MODIFICATION ---
                        } else {
                             // console.log(`[CFHelper Read] - Skipping style for option "${optionValue}" (Formula: ${formulaRead}) - not in validationOptions.`);
                        }
                    }
                    // --- END FIX ---
                    // Keep the old 'cellIs' reader logic commented or remove if fully switching
                    /*
                    else if (rule.type === 'cellIs' && rule.operator === 'equal' && rule.formulae?.length > 0 && rule.style) {
                       // ... old logic to parse T("...") or "..." ...
                    }
                    */
                });
            });

            // Populate the column's observable map from the found styles
            foundStyles.forEach((data, optionValue) => {
                // The data.style here should already be a plain object from the step above
                column.setOptionStyle(optionValue, data.style);
                console.log(`[CFHelper Read DBG] - Populating column.optionStyles for "${optionValue}" with:`, JSON.stringify(data.style));
            });
        }); // End runInAction

        console.log(`[CFHelper Read] Finished reading rules for column "${column.heading}". Styles populated:`, column.optionStyles);
    }

    /**
     * Clears conditional formatting rules managed by this helper (specific 'expression' type)
     * from the worksheet if their range overlaps with the specified target range.
     *
     * @param {ExcelJS.Worksheet} worksheet - The ExcelJS worksheet instance.
     * @param {number} columnIndex - The 1-based index of the column in the worksheet (used to define target range).
     * @param {number} startRow - The 1-based index of the first data row (used to define target range).
     * @param {number} endRow - The 1-based index of the last data row (used to define target range).
     */
    static clearConditionalFormatting(worksheet, columnIndex, startRow, endRow) {
        if (!worksheet || columnIndex < 1 || startRow > endRow) {
            console.warn('[CFHelper Clear] Invalid parameters for clearing conditional formatting.');
            return;
        }

        const columnLetter = this.colNumToLetter(columnIndex);
        const targetRangeString = `${columnLetter}${startRow}:${columnLetter}${endRow}`;
        console.log(`[CFHelper Clear] Clearing all conditional formatting rules for target range ${targetRangeString}`);

        const targetRange = this.parseRange(targetRangeString, worksheet);
        if (!targetRange) {
            console.warn(`[CFHelper Clear] Could not parse target range: ${targetRangeString}`);
            return;
        }

        const originalFormatting = worksheet.conditionalFormattings || [];
        const rulesToKeep = [];
        let removedCount = 0;
        let checkedRuleRefs = [];

        // Regex to identify our specific managed expression format
        const managedExpressionRegex = /^\$?([A-Z]+)\$?(\d+)\s*=\s*"(.*)"$/i;

        originalFormatting.forEach(cf => {
            const ruleRange = this.parseRange(cf.ref, worksheet);
            checkedRuleRefs.push(cf.ref); // Log refs being checked
            if (!ruleRange) {
                rulesToKeep.push(cf);
                return;
            }

            const overlaps = this.doRangesOverlap(targetRange, ruleRange);

            if (overlaps) {
                const isManagedRule = cf.rules.some(rule => 
                    // Check for our managed types
                    (rule.type === 'expression' && 
                     rule.formulae?.length > 0 && 
                     managedExpressionRegex.test(rule.formulae[0])) ||
                    // colorScale for rating
                    rule.type === 'colorScale' ||
                    // cellIs for checkbox (check for TRUE/FALSE formulae)
                    (rule.type === 'cellIs' && rule.operator === 'equal' && 
                     rule.formulae?.length > 0 && 
                     (rule.formulae[0] === true || rule.formulae[0] === false)) ||
                    // Other rule types we might add
                    rule.type === 'dataBar' || 
                    rule.type === 'iconSet'
                );

                if (isManagedRule) {
                    removedCount++;
                    console.log(`[CFHelper Clear] - Removing rule with ref "${cf.ref}" (type: ${cf.rules[0]?.type}) - overlaps target range ${targetRangeString} and is a managed rule type`);
                } else {
                    console.log(`[CFHelper Clear] - Keeping rule with ref "${cf.ref}" (type: ${cf.rules[0]?.type}) - overlaps target range ${targetRangeString} but NOT a managed type`);
                    rulesToKeep.push(cf);
                }
            } else {
                rulesToKeep.push(cf);
            }
        });

        console.log(`[CFHelper Clear] Checked rules with refs: ${checkedRuleRefs.join(', ')}`);
        // Only update if rules were actually removed
        if (removedCount > 0) {
            worksheet.conditionalFormattings = rulesToKeep;
            console.log(`[CFHelper Clear] Finished clearing for target ${targetRangeString}. Removed ${removedCount} rules.`);
        } else {
            console.log(`[CFHelper Clear] No overlapping managed rules found for target ${targetRangeString}.`);
        }
    }

    /**
     * Reapplies all conditional formatting for a worksheet based on the sheet model
     * @param {ExcelJS.Worksheet} worksheet - The worksheet to update
     * @param {import('../models/SheetModel').Sheet} sheetModel - The sheet model containing columns
     */
    static reapplyAllConditionalFormatting(worksheet, sheetModel) {
        if (!worksheet || !sheetModel || !sheetModel.columns) {
            console.warn('[CFHelper ReapplyAll] Invalid parameters for reapplying conditional formatting.');
            return;
        }
        
        console.log(`[CFHelper ReapplyAll] Reapplying all conditional formatting for sheet: ${sheetModel.name}`);
        
        const startRow = 2; // Data starts at row 2
        const endRow = sheetModel.rows.size + 1; // Last data row + 1 for header offset
        
        if (startRow > endRow) {
            console.log('[CFHelper ReapplyAll] No data rows to format');
            return;
        }
        
        // Process each column
        sheetModel.columns.forEach((column, colIndex) => {
            const excelColIndex = colIndex + 1;
            
            if (column.type.hasConditionalFormatting) {
                this.applyConditionalFormatting(worksheet, column, excelColIndex, startRow, endRow);
            } else {
                // Clean up any existing rules
                this.clearConditionalFormatting(worksheet, excelColIndex, startRow, endRow);
            }
        });
        
        console.log(`[CFHelper ReapplyAll] Completed reapplying conditional formatting for ${sheetModel.columns.length} columns`);
    }

    // --- Helper Functions ---

    /**
     * Converts a 1-based column index to its letter representation (A, B, ..., Z, AA, ...).
     * @param {number} colIndex - 1-based column index.
     * @returns {string} Column letter(s).
     */
    static colNumToLetter(colIndex) {
        let temp, letter = '';
        while (colIndex > 0) {
            temp = (colIndex - 1) % 26;
            letter = String.fromCharCode(temp + 65) + letter;
            colIndex = (colIndex - temp - 1) / 26;
        }
        return letter;
    }

    /**
     * Converts a column letter representation (A, B, ..., Z, AA, ...) to its 1-based index.
     * @param {string} colLetter - Column letter(s).
     * @returns {number} 1-based column index.
     */
    static colLetterToNum(colLetter) {
        let num = 0, len = colLetter.length;
        for (let i = 0; i < len; i++) {
            num = num * 26 + (colLetter.charCodeAt(i) - 64);
        }
        return num;
    }

    /**
     * Parses an Excel range string (e.g., "A1", "A1:C10", "$A$1:$C$10", "A:A", "1:1")
     * into an object with min/max row/col (1-based).
     * Uses worksheet dimensions for whole row/column references.
     * @param {string} rangeString - The Excel range string.
     * @param {ExcelJS.Worksheet} worksheet - The worksheet context for dimensions.
     * @returns {{minCol: number, maxCol: number, minRow: number, maxRow: number}|null} Parsed range or null on error.
     */
    static parseRange(rangeString, worksheet) {
        if (!rangeString || typeof rangeString !== 'string') return null;

        try {
            // Remove sheet name prefix if present (e.g., 'Sheet1'!A1:B2)
            const rangePart = rangeString.split('!').pop();

            // --- Handle whole column(s) (e.g., "A:A", "C:F") ---
            const colMatch = rangePart.match(/^(\$?([A-Z]+))\:(\$?([A-Z]+))$/i);
            if (colMatch) {
                const minCol = this.colLetterToNum(colMatch[2].toUpperCase());
                const maxCol = this.colLetterToNum(colMatch[4].toUpperCase());
                const maxRow = worksheet.rowCount || 1; // Use actual row count or default
                return { minCol, maxCol, minRow: 1, maxRow };
            }

            // --- Handle whole row(s) (e.g., "1:1", "3:5") ---
            const rowMatch = rangePart.match(/^(\$?(\d+))\:(\$?(\d+))$/i);
            if (rowMatch) {
                const minRow = parseInt(rowMatch[2], 10);
                const maxRow = parseInt(rowMatch[4], 10);
                const maxCol = worksheet.columnCount || 1; // Use actual col count or default
                return { minCol: 1, maxCol, minRow, maxRow };
            }

            // --- Handle single cell or range (e.g., "A1", "A1:C10", "$A$1") ---
            // Regex to capture the two potential parts of a range, ignoring $
            const cellRangeMatch = rangePart.match(/^(\$?([A-Z]+)\$?(\d+))(?:(\:)(\$?([A-Z]+)\$?(\d+)))?$/i);
            if (cellRangeMatch) {
                const minColLetter = cellRangeMatch[2].toUpperCase();
                const minRow = parseInt(cellRangeMatch[3], 10);
                const minCol = this.colLetterToNum(minColLetter);

                // Check if it's a range (group 4 is the colon)
                if (cellRangeMatch[4] === ':') {
                    const maxColLetter = cellRangeMatch[6].toUpperCase();
                    const maxRow = parseInt(cellRangeMatch[7], 10);
                    const maxCol = this.colLetterToNum(maxColLetter);
                    // Ensure min/max are ordered correctly
                    return {
                        minCol: Math.min(minCol, maxCol),
                        maxCol: Math.max(minCol, maxCol),
                        minRow: Math.min(minRow, maxRow),
                        maxRow: Math.max(minRow, maxRow)
                    };
                } else {
                    // Single cell
                    return { minCol, maxCol: minCol, minRow, maxRow: minRow };
                }
            }

            // --- Handle single whole column (e.g., "A") ---
            // This is less common in CF refs but possible
            const singleColMatch = rangePart.match(/^(\$?([A-Z]+))$/i);
            if (singleColMatch) {
                const col = this.colLetterToNum(singleColMatch[2].toUpperCase());
                const maxRow = worksheet.rowCount || 1;
                return { minCol: col, maxCol: col, minRow: 1, maxRow };
            }

            // --- Handle single whole row (e.g., "1") ---
            // This is less common in CF refs but possible
            const singleRowMatch = rangePart.match(/^(\$?(\d+))$/i);
            if (singleRowMatch) {
                const row = parseInt(singleRowMatch[2], 10);
                const maxCol = worksheet.columnCount || 1;
                return { minCol: 1, maxCol, minRow: row, maxRow: row };
            }


            console.warn(`[CFHelper ParseRange] Unrecognized range format: "${rangeString}"`);
            return null; // Format not recognized

        } catch (error) {
            console.error(`[CFHelper ParseRange] Error parsing range "${rangeString}":`, error);
            return null;
        }
    }

    /**
     * Checks if two parsed range objects overlap.
     * @param {{minCol: number, maxCol: number, minRow: number, maxRow: number}} range1
     * @param {{minCol: number, maxCol: number, minRow: number, maxRow: number}} range2
     * @returns {boolean} True if the ranges overlap, false otherwise.
     */
    static doRangesOverlap(range1, range2) {
        if (!range1 || !range2) return false;

        // Check for non-overlap in columns
        if (range1.maxCol < range2.minCol || range1.minCol > range2.maxCol) {
            return false;
        }

        // Check for non-overlap in rows
        if (range1.maxRow < range2.minRow || range1.minRow > range2.maxRow) {
            return false;
        }

        // If neither non-overlap condition is met, they must overlap
        return true;
    }

    // --- End Helper Functions ---
} 
